import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Client } from "src/types/api/Client";
import { PixKeyType } from "src/types/api/enums/PixKeyType";

export interface AuthResponse {
	accessToken: string;
	idToken: string;
	refreshToken: string;
	user: UserMe,
	expiresIn: number,
	anonymous: boolean,
	incompletedRegister: boolean,
	missingUserName: boolean
}

export interface Professional {
	type: "individual" | "juridico";
	rntrc?: string;
	cnpj?: string;
	termRead?: string;
	address?: {
		cep?: string;
		street?: string;
		number?: string;
		complement?: string;
		neighborhood?: string;
		city?: string;
		uf?: string;
	};
}

export interface UserMe {
	id: string,
	name: string,
	document: string,
	email: string,
	emailVerified: boolean,
	phoneNumber: string,
	phoneNumberVerified: boolean,
  communicationPreference?: string | null,
	pixKey?: string | null,
	pixKeyType?: PixKeyType | null,
	loggedWith?: string,
	notifications: {
		pn_email: boolean,
		pn_sms: boolean,
    pn_whatsapp: boolean
	},
	settings?: {
		fingerprint: boolean
	},
	address?: {
		cep?: string,
		uf?: string,
		city?: string,
		neighborhood?: string,
		address?: string,
		number?: string,
		complement?: string
	},
	professional?: Professional | null,
}

export interface AuthState {
	user: UserMe | null,
	accessToken: string | null,
	idToken: string | null,
	refreshToken: string | null,
	expiresIn: Date | null,
	anonymous: boolean | null,
	originalProfessional?: Professional | null,
}

const initialState: AuthState = {
	user: null,
	accessToken: null,
	idToken: null,
	refreshToken: null,
	expiresIn: null,
	anonymous: null
};

export const authSlice = createSlice({
	name: "auth",
	initialState,
	reducers: {
		login: (state, action: PayloadAction<AuthResponse>) => {
			// O /api/auth/refresh-token devolve o user sem endereço; sem esta guarda,
			// um refresh no meio do checkout apagava o address recém-salvo. Preserva o
			// address anterior quando o payload não o traz (mesma ideia do professional).
			const incomingUser = action.payload.user;
			const hasIncomingAddress = !!incomingUser?.address && Object.values(incomingUser.address).some(Boolean);
			const preservedAddress = hasIncomingAddress ? incomingUser.address : state.user?.address;

			state.accessToken = action.payload.accessToken;
			state.idToken = action.payload.idToken;
			state.user = incomingUser ? { ...incomingUser, address: preservedAddress } : incomingUser;
			state.expiresIn = new Date(Date.now() + action.payload.expiresIn * 1000);
			state.anonymous = action.payload.anonymous;
			state.originalProfessional = action.payload.user?.professional ?? null;

			if (action.payload.refreshToken)
				state.refreshToken = action.payload.refreshToken;
		},
		overrideUserProfessional: (state, action: PayloadAction<Professional | null>) => {
			if (state.user) {
				state.user.professional = action.payload;
			}
		},
		resetProfessionalToOriginal: (state) => {
			if (state.user) {
				state.user.professional = state.originalProfessional ?? null;
			}
		},
		updateProfile: (state, action: PayloadAction<Client>) => {
			state.user ??= {} as UserMe;

			state.user.name = action.payload.name;
			state.user.email = action.payload.email ?? "";
			state.user.emailVerified = action.payload.emailVerified ?? true;
			state.user.phoneNumber = action.payload.phoneNumber;
			state.user.phoneNumberVerified = action.payload.phoneNumberVerified ?? true;
        
      if (action.payload.communicationPreference !== undefined) {
        state.user.communicationPreference = action.payload.communicationPreference || undefined;
      }

      if (action.payload.pixKey !== undefined) {
        state.user.pixKey = action.payload.pixKey;
        state.user.pixKeyType = action.payload.pixKeyType;
      }

      if (action.payload.contactPreferences) {
          state.user.notifications = {
              pn_email: !!action.payload.contactPreferences.pn_email,
              pn_sms: !!action.payload.contactPreferences.pn_sms,
              pn_whatsapp: !!action.payload.contactPreferences.pn_whatsapp,
          };
      }
      
			state.user.address = {
				cep: action.payload.address?.cep,
				uf: action.payload.address?.uf,
				city: action.payload.address?.city,
				neighborhood: action.payload.address?.neighborhood,
				address: action.payload.address?.address,
				number: action.payload.address?.number,
				complement: action.payload.address?.complement,
			};
		},
		updateAddress: (state, action: PayloadAction<NonNullable<UserMe["address"]>>) => {
			if (state.user)
				state.user.address = action.payload;
		},
		setPixKey: (state, action: PayloadAction<{ pixKey: string; pixKeyType: PixKeyType }>) => {
			if (state.user) {
				state.user.pixKey = action.payload.pixKey;
				state.user.pixKeyType = action.payload.pixKeyType;
			}
		},
		logout: (state) => {
			state.user = null;
			state.idToken = null;
			state.accessToken = null;
			state.refreshToken = null;
			state.anonymous = null;
		},
		markUserWithPhoneNumberVerified: (state) => {
			if (state.user)
				state.user.phoneNumberVerified = true;
		}
	},
});

export const { login, logout, updateProfile, updateAddress, setPixKey, markUserWithPhoneNumberVerified, overrideUserProfessional, resetProfessionalToOriginal } = authSlice.actions;
export default authSlice.reducer;
